import { FC, useState } from 'react'
import { Copy, Check, RotateCcw, Pencil } from 'lucide-react'
import { Button } from '../ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { cn } from '../../lib/utils'

interface MessageActionsProps {
  role: 'user' | 'assistant'
  content: string
  isGenerating?: boolean
  onRetry?: () => void
  onEdit?: () => void
  className?: string
}

/**
 * Reusable message action buttons — Copy (assistant), Edit (user), Retry (both).
 * Visibility is controlled by the parent; the actions themselves are always rendered when present.
 */
export const MessageActions: FC<MessageActionsProps> = ({
  role,
  content,
  isGenerating = false,
  onRetry,
  onEdit,
  className
}) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  return (
    <div className={cn(
      "flex items-center gap-1",
      className
    )}>
      {/* Copy button - assistant messages only */}
      {role === 'assistant' && content && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              disabled={isGenerating}
              aria-label={copied ? 'Copied' : 'Copy response'}
              className="h-7 w-7 p-0 rounded-lg"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-clinical-sage" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">{copied ? 'Copied!' : 'Copy response'}</p>
          </TooltipContent>
        </Tooltip>
      )}

      {/* Edit button - user messages only */}
      {role === 'user' && onEdit && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              disabled={isGenerating}
              aria-label="Edit message"
              className="h-7 w-7 p-0 rounded-lg"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Edit message</p>
          </TooltipContent>
        </Tooltip>
      )}

      {/* Retry button - both roles */}
      {onRetry && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRetry}
              disabled={isGenerating}
              aria-label="Retry"
              className="h-7 w-7 p-0 rounded-lg"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Retry</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}
